import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, take, tap } from 'rxjs/operators';
import { Account } from '../../models/account/account.model';
import { Coupon } from '../../models/core/coupon.model';
import { CouponService } from '../core/coupon.service';
import { HypermediaService } from '../core/hypermedia.service';
import { AccountService } from './account.service';
import { HttpClient } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class AccountCouponsService {
  private coupons: BehaviorSubject<Coupon[]> = new BehaviorSubject([]);
  public coupons$: Observable<Coupon[]> = this.coupons.asObservable();

  private fetchCoupons: Subject<void> = new Subject<void>();

  constructor(
    private accountService: AccountService,
    private hypermedia: HypermediaService,
    private couponService: CouponService,
    private http: HttpClient
  ) {
    combineLatest([
      this.accountService.account$.pipe(
        filter((account: Account) => account.isInstantiated),
        take(1)
      ),
      this.fetchCoupons.asObservable(),
    ])
      .pipe(
        mergeMap(([account]) => this.hypermedia.get(account.links, 'coupons')),
        map((json: Object[]) => (json || []).map(c => new Coupon().deserialize(c))),
        map((coupons: Coupon[]) =>
          coupons.sort((a, b) => {
            const a_app = this.couponService.isApplied(a);
            const b_app = this.couponService.isApplied(b);

            if (a_app && !b_app) {
              return -1;
            }
            if (!a_app && b_app) {
              return 1; // Move b before a
            }
            return 0;
          })
        ),
        catchError(() => of([]))
      )
      .subscribe((coupons: Coupon[]) => this.coupons.next(coupons));

    this.reloadCoupons();
  }

  public unredeemOptionalLoyaltyCoupon(instanceCode: string): Observable<void> {
    if (!instanceCode) {
      return throwError('No instance code provided');
    }

    return this.http.post<void>(`/ws/integrated/v1/ordering/account/coupons/unredeem/${instanceCode}`, {}).pipe(
      tap(() => this.reloadCoupons()),
      catchError(error => {
        let errorMessage = 'Something went wrong. Please try again later.';
        if (error?.error?.message) {
          errorMessage = error.error.message;
        }
        return throwError(errorMessage);
      })
    );
  }

  public reloadCoupons(): void {
    this.fetchCoupons.next();
  }
}
